import type { AppDefinition, FeatureModule, ModuleAudience } from '@/platform/app/contracts';
import { useAppSelector } from '@/platform/store/types';

/** Who is looking at the dashboard: a customer, or a staff member of this platform. */
export type Viewer = 'user' | 'internal';

/**
 * `type_user` from the session. Staff accounts belong to no organization and see the staff console
 * instead of the customer modules (backend: docs/architecture/internal-users.md).
 */
export const viewerOf = (typeUser: string | undefined): Viewer =>
  typeUser === 'internal_user' ? 'internal' : 'user';

export const useViewer = (): Viewer =>
  useAppSelector((state) => viewerOf(state.auth.user?.type_user));

/** A contribution without an audience is for customers: every module written before staff existed. */
export const isVisibleTo = (audience: ModuleAudience | undefined, viewer: Viewer): boolean => {
  const target = audience ?? 'user';
  return target === 'all' || target === viewer;
};

export const modulesFor = (app: AppDefinition, viewer: Viewer): FeatureModule[] =>
  app.modules.filter((module) => isVisibleTo(module.audience, viewer));

/** Staff land on the console; a dashboard that ships none sends them to the customer landing. */
export const landingFor = (app: AppDefinition, viewer: Viewer): string =>
  viewer === 'internal' ? (app.routes.internalLanding ?? app.routes.landing) : app.routes.landing;
